import React, { useCallback } from 'react';

import { formatLumberGrade, LumberPrice } from '@paragon/contract-library';
import { naturalSort } from '@paragon/utilities';

import { editLumberPrices } from '../actions/lumberPriceActions';
import { formatDateTime, removeUnderscores } from './formattingUtilities';
import { useAppDispatch, useAppSelector } from './hooks';
import { InsertLumberPrice } from './InsertLumberPrice';
import { ColumnData, PriceGrid } from './price-grid/PriceGrid';
import { parsePriceEditValue } from './price-grid/priceGridUtilities';

const columns: ColumnData<LumberPrice>[] = [
  {
    key: 'species',
    title: 'Species',
    width: 125,
    getCellValue: (row) => row.species.replace(/_/g, ' '),
    filterType: 'select',
    filterLabel: 'All species',
    groupable: true,
  },
  {
    key: 'grade',
    title: 'Grade',
    width: 100,
    getCellValue: (row) => formatLumberGrade(row.grade, []),
    filterType: 'select',
    filterLabel: 'All grades',
    groupable: true,
  },
  {
    key: 'treatmentType',
    title: 'Treatment Type',
    width: 175,
    getCellValue: (row) => removeUnderscores(row.treatmentType),
    filterType: 'select',
    filterLabel: 'Any treatment',
    groupable: true,
  },
  {
    key: 'nominalDimensions',
    title: 'Nominal Dimensions',
    width: 200,
    getCellValue: (row) => `${row.nominalThicknessInInches}x${row.nominalWidthInInches}`,
    // Applying naturalSort to columns other than ones that need it produces undesired results
    compare: naturalSort,
    filterType: 'select',
    filterLabel: 'Any dimensions',
    groupable: true,
  },
  {
    key: 'length',
    title: 'Length (FT)',
    width: 125,
    getCellValue: (row) => Number((row.lengthInInches / 12).toFixed(8)).toString(),
    sortByMapping: (row) => row.lengthInInches,
    // Rows sort by the numeric sortByMapping; compare orders the filter dropdown options,
    // which would otherwise be lexicographic (10, 12, 8).
    compare: naturalSort,
    filterType: 'select',
    filterLabel: 'Any length',
    groupable: true,
  },
  {
    key: 'pricePerThousandBoardFeet',
    title: 'Price for 1,000 BF',
    width: 175,
    getCellValue: (row) => `$${row.pricePerThousandBoardFeet.toFixed(2)}`,
    getUpdatedRowWithEdit: (row, newValue) => ({
      ...row,
      pricePerThousandBoardFeet: parsePriceEditValue(newValue),
    }),
    getEditValue: (row) => row.pricePerThousandBoardFeet.toFixed(2),
    editStartAdornment: '$',
    validateEditValue: (newValue) => {
      const parsedValue = Number(newValue);
      if (newValue.trim() === '' || !Number.isFinite(parsedValue) || parsedValue < 0) {
        return 'Enter a non-negative price.';
      }
      return undefined;
    },
    sortByMapping: (row) => row.pricePerThousandBoardFeet,
    filterType: 'range',
    rangeFilterLabels: { min: 'Min price', max: 'Max price' },
  },
  {
    key: 'priceDateTime',
    title: 'Last Updated',
    width: 175,
    getCellValue: (row) => formatDateTime(row.priceDateTime),
    sortByMapping: (row) => Date.parse(row.priceDateTime),
  },
  {
    key: 'notes',
    title: 'Notes',
    width: 200,
    getCellValue: (row) => row.notes ?? '',
    getUpdatedRowWithEdit: (row, newValue) => ({ ...row, notes: newValue === '' ? null : newValue }),
    filterType: 'text',
    filterLabel: 'Search notes',
  },
];

function warnOnUneditableChanges(editedRow: LumberPrice, originalRow: LumberPrice | undefined) {
  if (!originalRow) return;

  for (const [key, value] of Object.entries(editedRow)) {
    if (key === 'pricePerThousandBoardFeet' || key === 'notes') continue;

    if (originalRow[key as keyof LumberPrice] !== value) {
      console.error(`The ${key} value was edited on the lumber price. Currently only price and notes can be edited.`);
    }
  }
}

export function LumberPriceGrid() {
  const dispatch = useAppDispatch();
  const lumberPrices = useAppSelector((state) => state.lumberPrices);

  const commitRowEdits = useCallback(
    (editedRows: readonly LumberPrice[]) => {
      for (const editedRow of editedRows) {
        warnOnUneditableChanges(editedRow, lumberPrices[editedRow.guid]);
      }

      return dispatch(editLumberPrices(editedRows));
    },
    [dispatch, lumberPrices],
  );

  return (
    <PriceGrid
      rows={Object.values(lumberPrices)}
      columns={columns}
      commitRowEdits={commitRowEdits}
      insertBar={(onClose, onHeightChange) => <InsertLumberPrice onClose={onClose} onHeightChange={onHeightChange} />}
      defaultSorting={[
        { columnKey: 'species', direction: 'asc' },
        { columnKey: 'grade', direction: 'asc' },
        { columnKey: 'treatmentType', direction: 'asc' },
        { columnKey: 'nominalDimensions', direction: 'asc' },
        { columnKey: 'length', direction: 'asc' },
      ]}
    />
  );
}
